import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, X, RotateCcw } from "lucide-react";
import { CONTENT_DEFAULTS } from "../../data/contentDefaults";
import { mergeContent, setContentCache } from "../../lib/content";
import ImageUpload from "./ImageUpload";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#3b9dff] focus:outline-none";
const clone = (o) => JSON.parse(JSON.stringify(o));
const getAt = (o, path) => path.reduce((a, k) => a?.[k], o);
const setAt = (o, path, v) => { const c = clone(o); let t = c; path.slice(0, -1).forEach((k) => { t = t[k]; }); t[path[path.length - 1]] = v; return c; };
const tid = (path) => `content-${path.join("-")}`;

const Text = ({ c, setC, path, label, long }) => {
  const v = getAt(c, path) ?? "";
  const Tag = long ? "textarea" : "input";
  return (
    <label className="block text-xs font-semibold text-slate-500">
      {label}
      <Tag rows={long ? 3 : undefined} value={v} onChange={(e) => setC(setAt(c, path, e.target.value))} data-testid={tid(path)} className={`${field} mt-1 font-normal text-[#0a1f44]`} />
    </label>
  );
};

const StringList = ({ c, setC, path, label, long }) => {
  const list = getAt(c, path) || [];
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <div className="mt-1 space-y-2">
        {list.map((_, i) => (
          <div key={i} className="flex gap-2">
            <div className="flex-1"><Text c={c} setC={setC} path={[...path, i]} label="" long={long} /></div>
            <button type="button" onClick={() => setC(setAt(c, path, list.filter((__, j) => j !== i)))} data-testid={`${tid(path)}-remove-${i}`} className="p-2 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
          </div>
        ))}
        <button type="button" onClick={() => setC(setAt(c, path, [...list, ""]))} data-testid={`${tid(path)}-add`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3b9dff]"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
    </div>
  );
};

const ObjList = ({ c, setC, path, label, fields, blank, fixed }) => {
  const list = getAt(c, path) || [];
  return (
    <div>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <div className="mt-2 space-y-3">
        {list.map((_, i) => (
          <div key={i} className="relative rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2">
            {!fixed && <button type="button" onClick={() => setC(setAt(c, path, list.filter((__, j) => j !== i)))} data-testid={`${tid(path)}-remove-${i}`} className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>}
            {fields.map(([k, l, kind]) => (kind === "list"
              ? <StringList key={k} c={c} setC={setC} path={[...path, i, k]} label={l} />
              : <Text key={k} c={c} setC={setC} path={[...path, i, k]} label={l} long={kind === "long"} />))}
          </div>
        ))}
        {!fixed && <button type="button" onClick={() => setC(setAt(c, path, [...list, clone(blank)]))} data-testid={`${tid(path)}-add`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#3b9dff]"><Plus className="w-3.5 h-3.5" /> Add</button>}
      </div>
    </div>
  );
};

const PriceInput = ({ c, setC, path, label, min = "1" }) => (
  <label className="block text-xs font-semibold text-slate-500">
    {label}
    <span className="mt-1 flex items-center gap-1"><span className="text-slate-400">$</span>
      <input type="number" min={min} value={getAt(c, path) ?? ""} onChange={(e) => setC(setAt(c, path, e.target.value === "" ? "" : Number(e.target.value)))} data-testid={tid(path)} className={`${field} font-normal text-[#0a1f44]`} />
    </span>
  </label>
);

const Section = ({ title, hint, sectionKey, also = [], c, setC, children }) => (
  <section data-testid={`content-section-${sectionKey}`} className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
    <div className="flex items-start gap-3">
      <div className="flex-1"><h3 className="font-bold text-[#0a1f44]">{title}</h3>{hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}</div>
      <button type="button" onClick={() => setC({ ...c, ...Object.fromEntries([sectionKey, ...also].map((k) => [k, clone(CONTENT_DEFAULTS[k])])) })} data-testid={`content-reset-${sectionKey}`} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[#0a1f44]"><RotateCcw className="w-3.5 h-3.5" /> Reset to original</button>
    </div>
    {children}
  </section>
);

const Photo = ({ c, setC, path, label, textKey = "alt", textLabel = "Description (for Google and screen readers)", extra }) => (
  <div data-testid={`${tid(path)}-photo`} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-2">
    <ImageUpload value={getAt(c, [...path, "src"])} onChange={(url) => url && setC(setAt(c, [...path, "src"], url))} label={label} testid={`${tid(path)}-upload`} />
    <Text c={c} setC={setC} path={[...path, textKey]} label={textLabel} />
    {extra && <Text c={c} setC={setC} path={[...path, extra[0]]} label={extra[1]} />}
  </div>
);

const SIZE_LABELS = ["Up to 65 inches", "66 to 85 inches", "86 to 100 inches"];
const MOUNT_CARDS = [["own", "Already have a mount"], ["flat", "Flat Mount"], ["tilt", "Tilt Mount"], ["full", "Full-Motion Mount"], ["spec", "Specialty and electric"]];
const MOUNT_ROWS = [
  ["Own mount, included mount value deducted", ["mounts", "flatValue"]],
  ["Full-Motion Mount upgrade", ["mounts", "fullMotion"]],
];
const ADDONS = [["brick", "Brick wall"], ["tile", "Tile wall"], ["stone", "Stone or concrete wall"], ["fireplace", "Above a fireplace"], ["channel", "Surface channeling"], ["inwall", "In-wall hiding"], ["powerkit", "Power kit"]];
const EXTRA_FIELDS = [["soundbar", "Soundbar mounting"], ["surround", "Surround-sound speaker mounting (up to 2)"], ["streaming", "Streaming device setup"], ["remote", "Universal remote programming"], ["smarthome", "Smart-home and voice setup"], ["receiver", "AV receiver setup"], ["led", "LED backlighting strip (install only)"], ["shelf", "Floating component shelf"], ["ethernet", "Ethernet / network drop"]];
const PctInput = ({ c, setC, path, label }) => (
  <label className="block text-xs font-semibold text-slate-500">
    {label}
    <span className="mt-1 flex items-center gap-1">
      <input type="number" min="0" max="15" step="0.01" value={getAt(c, path) ?? ""} onChange={(e) => setC(setAt(c, path, e.target.value === "" ? "" : Number(e.target.value)))} data-testid={tid(path)} className={`${field} font-normal text-[#0a1f44]`} />
      <span className="text-slate-400">%</span>
    </span>
  </label>
);

export const AdminContent = ({ token }) => {
  const [c, setC] = useState(null);
  const [st, setSt] = useState({ busy: false, msg: "" });
  useEffect(() => { fetch(`${API}/content`).then((r) => r.json()).then((o) => setC(mergeContent(CONTENT_DEFAULTS, o || {}))); }, []);
  const set = (v) => { setC(v); setSt({ busy: false, msg: "" }); };

  const save = async () => {
    setSt({ busy: true, msg: "" });
    const r = await fetch(`${API}/admin/content`, { method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(c) });
    const d = await r.json();
    if (!r.ok) return setSt({ busy: false, msg: typeof d.detail === "string" ? d.detail : "Could not save" });
    setContentCache(d);
    setSt({ busy: false, msg: "Saved. Live on the site now." });
  };

  if (!c) return <p className="text-slate-500">Loading content...</p>;
  const p = { c, setC: set };
  return (
    <div data-testid="admin-content" className="space-y-5 pb-24">
      <p className="text-sm text-slate-600">Edit the text and install prices below, then click Save. Changes go live right away. You can use {"{phone}"}, {"{mountFrom}"} and {"{gallery}"} in any text, and they fill in automatically.</p>
      <Section title="Install prices" hint="Tier price per TV size, Flat Mount or Tilt Mount included. Over 100 inches is always custom quote. Used in the quote wizard, the pricing cards, and the Gallery page." sectionKey="prices" {...p}>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {SIZE_LABELS.map((l, i) => <PriceInput key={l} {...p} path={["prices", "sizes", i]} label={`Mount It, ${l}`} />)}
          <PriceInput {...p} path={["prices", "gallery"]} label="Gallery (Frame / G-series)" />
        </div>
      </Section>
      <Section title="Photos" hint="Upload a new photo to replace one. Square photos look best. Your other photos stay as they are." sectionKey="photos" {...p}>
        <div className="grid md:grid-cols-2 gap-4">
          <Photo {...p} path={["photos", "hero"]} label="Homepage hero photo" extra={["caption", "Caption on the photo (leave blank for none)"]} />
          <Photo {...p} path={["photos", "whiteGlove"]} label="White Glove page photo" />
        </div>
        <p className="text-xs font-semibold text-slate-500 pt-2">Homepage "Real installs" row</p>
        <div className="grid md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Photo key={i} {...p} path={["photos", "strip", i]} label={`Install photo ${i + 1}`} />)}
        </div>
        <p className="text-xs font-semibold text-slate-500 pt-2">Gallery page photos (photo 1 and 2 are also the before and after slider)</p>
        <div className="grid md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <Photo key={i} {...p} path={["photos", "gallery", i]} label={["Before photo", "After photo", "Third photo"][i]} textKey="cap" textLabel="Caption" />)}
        </div>
      </Section>
      <Section title="Wall and concealment add-ons" hint="Added on top of the tier price in the quote wizard. Plaster and lath stays as an estimate confirmed from photos." sectionKey="addons" {...p}>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ADDONS.map(([k, l]) => <PriceInput key={k} {...p} path={["addons", k]} label={l} min="0" />)}
        </div>
      </Section>
      <Section title="Extras (Anything else? step)" hint="Added at full price to the quote total the moment a customer checks one. Promos and multi-TV discounts never apply to extras." sectionKey="extras" {...p}>
        <div className="grid sm:grid-cols-3 gap-3">
          {EXTRA_FIELDS.map(([k, l]) => <PriceInput key={k} {...p} path={["extras", k]} label={l} min="0" />)}
        </div>
      </Section>
      <Section title="NC sales tax by county" hint="Combined state and local rate, from the NC Department of Revenue (loaded with rates effective July 1, 2026). The quote uses the customer's zip to find the county and applies this rate to the quote total." sectionKey="taxRates" {...p}>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {Object.keys(CONTENT_DEFAULTS.taxRates).map((k) => <PctInput key={k} {...p} path={["taxRates", k]} label={`${k} County`} />)}
        </div>
      </Section>
      <Section title="Site survey fallback" hint="Shown on every quote step and the quote result. Use {survey} for the price." sectionKey="survey" {...p}>
        <PriceInput {...p} path={["survey", "price"]} label="Survey price" />
        <Text {...p} path={["survey", "fallback"]} label="Fallback link text" />
        <Text {...p} path={["survey", "terms"]} label="Survey terms" long />
      </Section>
      <Section title="TV bundle step" hint="The 'Buying your TV from us?' step in the quote wizard." sectionKey="bundle" {...p}>
        <Text {...p} path={["bundle", "text"]} label="Bundle text" long />
        <Text {...p} path={["bundle", "match"]} label="Price-match rule" long />
        <Text {...p} path={["bundle", "note"]} label="TV price note" long />
      </Section>
      <Section title="Mounts (TV quote wizard)" hint="Photos, card text, and mount prices used in the Mount step. Flat Mount and Tilt Mount are always included at $0." sectionKey="mounts" {...p}>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {MOUNT_CARDS.map(([k, l]) => (
            <Photo key={k} {...p} path={["mounts", "cards", k]} label={`${l} card photo`} textKey="label" textLabel="Card title" extra={["sub", "One-line description"]} />
          ))}
        </div>
        <div className="overflow-x-auto pt-2">
          <table className="w-full text-sm" data-testid="mount-price-table">
            <thead>
              <tr className="text-xs text-slate-500">
                <th className="text-left font-semibold py-2 pr-3">Price</th>
                {SIZE_LABELS.map((l) => <th key={l} className="text-left font-semibold py-2 pr-3 whitespace-nowrap">{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {MOUNT_ROWS.map(([label, path]) => (
                <tr key={label} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-semibold text-[#0a1f44] whitespace-nowrap">{label}</td>
                  {SIZE_LABELS.map((l, i) => <td key={l} className="py-2 pr-3 min-w-[90px]"><PriceInput {...p} path={[...path, i]} label="" min="0" /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Homepage hero" sectionKey="hero" {...p}>
        <Text {...p} path={["hero", "eyebrow"]} label="Small line above the headline" />
        <Text {...p} path={["hero", "title"]} label="Headline" />
        <Text {...p} path={["hero", "subtitle"]} label="Text under the headline" long />
        <StringList {...p} path={["hero", "chips"]} label="Pills" />
        <Text {...p} path={["hero", "ctaLabel"]} label="Text button label" />
      </Section>
      <Section title="Brands band" sectionKey="brandsBand" {...p}>
        <Text {...p} path={["brandsBand", "heading"]} label="Heading" />
        <Text {...p} path={["brandsBand", "text"]} label="Text (the phone number is added after it)" long />
      </Section>
      <Section title="How it works" sectionKey="howItWorks" {...p}>
        <Text {...p} path={["howItWorks", "eyebrow"]} label="Label" />
        <Text {...p} path={["howItWorks", "heading"]} label="Heading" />
        <ObjList {...p} path={["howItWorks", "steps"]} label="Steps" fields={[["t", "Step title"], ["d", "Step text", "long"]]} blank={{ t: "", d: "" }} />
      </Section>
      <Section title="Two Ways to Get Your Quote" sectionKey="quoting" {...p}>
        <Text {...p} path={["quoting", "heading"]} label="Heading" />
        <ObjList {...p} fixed path={["quoting", "options"]} label="Options" fields={[["title", "Title"], ["body", "Text", "long"]]} />
        <Text {...p} path={["quoting", "ctaLabel"]} label="Button label" />
        <Text {...p} path={["quoting", "surveyNote"]} label="Line under the photo form submit button" />
      </Section>
      <Section title="Pricing cards" hint="The links and buttons on each card stay with its position (2nd = Gallery, 3rd = White Glove, 4th = Surround)." sectionKey="pricing" {...p}>
        <Text {...p} path={["pricing", "eyebrow"]} label="Label" />
        <Text {...p} path={["pricing", "heading"]} label="Heading" />
        <ObjList {...p} fixed path={["pricing", "plans"]} label="Cards" fields={[["name", "Name"], ["price", "Price line"], ["tag", "Small tag (optional)"], ["pts", "Bullet points", "list"]]} />
      </Section>
      <Section title="5-Year Install Warranty" hint="Shows on the homepage, the footer, and the quote, Gallery, White Glove and Surround pages." sectionKey="warranty" {...p}>
        <Text {...p} path={["warranty", "heading"]} label="Heading" />
        <Text {...p} path={["warranty", "text"]} label="Warranty text" long />
      </Section>
      <Section title="Reviews and service areas" sectionKey="reviews" also={["areas"]} {...p}>
        <Text {...p} path={["reviews", "heading"]} label="Reviews heading" />
        <Text {...p} path={["reviews", "text"]} label="Reviews text" long />
        <Text {...p} path={["areas", "text"]} label="Service area line" />
        <StringList {...p} path={["areas", "towns"]} label="Town buttons (towns with a page link to it automatically)" />
      </Section>
      <Section title="Final call to action" sectionKey="finalCta" {...p}>
        <Text {...p} path={["finalCta", "heading"]} label="Heading" />
        <Text {...p} path={["finalCta", "buttonLabel"]} label="Button label" />
      </Section>
      <Section title="Service area page FAQs" hint="Shown on every town page. Use {city}, {county}, {region} and {nearby} to fill in each town's details." sectionKey="areaFaq" {...p}>
        <ObjList {...p} path={["areaFaq"]} label="Questions" fields={[["q", "Question"], ["a", "Answer", "long"]]} blank={{ q: "", a: "" }} />
      </Section>
      <div className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200">
        <div className="max-w-[1280px] mx-auto px-5 py-3 flex items-center gap-4">
          <button onClick={save} disabled={st.busy} data-testid="content-save-btn" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-bold disabled:opacity-60">
            {st.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save and publish
          </button>
          {st.msg && <span data-testid="content-save-msg" className={`text-sm ${st.msg.startsWith("Saved") ? "text-green-600" : "text-red-600"}`}>{st.msg}</span>}
        </div>
      </div>
    </div>
  );
};
